import React, { useEffect, useState } from "react";
import { Tabs, TabList, TabPanels, Tab, TabPanel, Box } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import CertificateTab from "./Tabs/certificateTab";
import EnrolledTab from "./Tabs/enrolledTab";
import CompletedTab from "./Tabs/completedTab";
import WatchLaterTab from "./Tabs/watchLaterTab";
import { useMyLearningContext } from "@root/context/my-learning-context";
import useMediaQ from "@root/hooks/useMediaQ";
import {
  COURSE_ENROLLMENT_STATUS,
  LEARNING_TAB_TYPES,
  DEFAULT_LEARNING_TAB_INDEX,
} from "@root/constants";
import MobileTabs, { TabItem } from "@root/components/common/MobileTabs";

const TabIconLabel = ({ label }: { label: string }) => {
  return (
    <Box display="flex" alignItems="center" gap={2} fontWeight="bold">
      {label}
    </Box>
  );
};

const MyLearningTabs: React.FC = () => {
  const { t } = useTranslation("common");
  const { fetchMyLearning, setPageNo } = useMyLearningContext();
  const { sm, isMobileView } = useMediaQ();
  const isMobile = !sm;
  const location = useLocation();

  // Get tab parameter from URL
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get("tab");
  console.log("tabParam", tabParam);

  const getInitialTabIndex = () => {
    const stateTabIndex = location.state?.activeTabIndex;
    if (stateTabIndex !== undefined) {
      return stateTabIndex;
    }

    return DEFAULT_LEARNING_TAB_INDEX;
  };

  const [selectedTabIndex, setSelectedTabIndex] =
    useState(getInitialTabIndex());

  const tabData: TabItem[] = [
    {
      id: "enrolled",
      label: isMobile
        ? t("myLearningDashboard.mobileEnrolled")
        : t("myLearningDashboard.mobileEnrolled"),
      payload: { status: COURSE_ENROLLMENT_STATUS.ENROLLED },
    },
    {
      id: LEARNING_TAB_TYPES.COMPLETED,
      label: t("myLearningDashboard.completed"),
      payload: { status: COURSE_ENROLLMENT_STATUS.COMPLETED },
    },
    // {
    //   id: LEARNING_TAB_TYPES.WATCH_LATER,
    //   label: t("myLearningDashboard.watchLater"),
    //   payload: { isWatchLater: true },
    // },
    {
      id: LEARNING_TAB_TYPES.CERTIFICATES,
      label: t("myLearningDashboard.certificates"),
    },
  ];

  const handleTabChange = (index: number) => {
    setSelectedTabIndex(index);
    const selectedTab = tabData[index];
    // Reset to page 1 when switching tabs
    setPageNo(1);
    if (selectedTab?.payload) {
      fetchMyLearning(selectedTab?.payload, 1);
    }
  };

  const renderTabs = () => {
    if (isMobileView && isMobile)
      return (
        <MobileTabs
          tabs={tabData}
          selectedIndex={selectedTabIndex}
          onTabChange={handleTabChange}
          variant="flex"
        />
      );
    else
      return (
        <TabList
          mb={4}
          w="100%"
          background="linear-gradient(0deg, rgba(205, 214, 220, 0.32), rgba(205, 214, 220, 0.32))"
          h="51px"
          borderRadius="10px"
          p={1}
          display="flex"
          boxShadow="sm"
          overflowX="auto"
          whiteSpace="nowrap"
        >
          {tabData?.map((tab, idx) => (
            <Tab
              key={tab.label}
              data-tour-item={
                tab.id === "enrolled"
                  ? "journalist-my-learning-enrolled"
                  : tab.id === LEARNING_TAB_TYPES.COMPLETED
                    ? "journalist-my-learning-completed"
                    : tab.id === LEARNING_TAB_TYPES.WATCH_LATER
                      ? "journalist-my-learning-watch-later"
                      : tab.id === LEARNING_TAB_TYPES.CERTIFICATES
                        ? "journalist-my-learning-certificates"
                        : undefined
              }
              fontWeight="600"
              fontSize="18px"
              color="gray.700"
              _selected={{
                color: "#1819B4",
                bg: "white",
                border: "0.5px solid #A7B2BA",
                boxShadow: "0px 1px 4px 0px #0000001F",
                borderRadius: "10px",
              }}
              _focus={{ boxShadow: "none" }}
              py={4}
              px={6}
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={2}
              textColor={"#2F2F34"}
              transition="all 0.2s"
              borderRadius="10px"
              minW="180px"
            >
              <TabIconLabel label={tab.label} />
            </Tab>
          ))}
        </TabList>
      );
  };

  useEffect(() => {
    const initialTab = tabData[selectedTabIndex];
    setPageNo(1);
    if (initialTab?.payload) {
      fetchMyLearning(initialTab?.payload, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTabIndex]);

  return (
    <Box w="100%" maxW="full" overflowY={"auto"} mx="auto">
      <Tabs
        variant="unstyled"
        isFitted
        w="100%"
        index={selectedTabIndex}
        onChange={handleTabChange}
      >
        {/* Render tabs */}
        {renderTabs()}
        <TabPanels>
          <TabPanel px={0}>
            <Box mx="auto" display="flex" justifyContent="center">
              <EnrolledTab />
            </Box>
          </TabPanel>
          <TabPanel px={0}>
            <Box mx="auto" display="flex" justifyContent="center">
              <CompletedTab />
            </Box>
          </TabPanel>
          <TabPanel px={0}>
            <Box mx="auto" display="flex" justifyContent="center">
              <WatchLaterTab />
            </Box>
          </TabPanel>
          <TabPanel px={0}>
            <Box mx="auto" display="flex" justifyContent="center">
              <CertificateTab />
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default MyLearningTabs;
